import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Send, Loader2, ShieldAlert, Sparkles, Clock, Star, StarOff, Download, Printer, Copy, Filter, Search } from 'lucide-react';

/**
 * Chat/Q&A técnico (genérico, sem vínculo a paciente)
 * Incrementos nesta versão (v3):
 * - Botões mais destacados (estilo sólido, maior contraste e sombra suave)
 * - Favoritos: campo de busca/filtro
 * - Presets por temas (chips): Biossegurança, Materiais, Urgências, Documentação
 * - Exportar em Markdown (.md), além de TXT e PDF (impressão)
 */
const FAVORITES_KEY = 'oa_ia_chat_favorites_v1';

const AIChatAssistant = ({ backendUrl, token, toast }) => {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('detalhada'); // curta | detalhada | guia
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      text:
        'Olá! Sou o Assistente Técnico do OdontoAssist AI. ' +
        'Envie sua dúvida técnica (ex.: condutas, protocolos, materiais, biossegurança). ' +
        'Evito diagnósticos e receitas específicas sem avaliação clínica.',
      meta: { ts: new Date().toISOString() }
    }
  ]);
  const [favorites, setFavorites] = useState([]);
  const [favQuery, setFavQuery] = useState('');
  const [activeTheme, setActiveTheme] = useState('todos'); // todos | biosseguranca | materiais | urgencias | documentacao
  const scrollerRef = useRef(null);

  // Thematic presets
  const THEMES = {
    todos: 'Todos',
    biosseguranca: 'Biossegurança',
    materiais: 'Materiais',
    urgencias: 'Urgências',
    documentacao: 'Documentação'
  };

  const PRESETS_MAP = {
    biosseguranca: [
      'Quais cuidados de biossegurança devo reforçar em procedimentos com aerosol?',
      'Como otimizar a desinfecção de superfícies e instrumentais entre atendimentos?',
      'Boas práticas de paramentação e descarte de EPI em clínica odontológica?'
    ],
    materiais: [
      'Critérios técnicos para indicação de restauração versus inlay/onlay?',
      'Adesão: quais cuidados com condicionamento/primer/bond em diferentes substratos?',
      'Cimentos provisórios vs. definitivos: quando escolher cada um?'
    ],
    urgencias: [
      'Como conduzir urgências odontológicas (dor aguda) de forma segura e conservadora?',
      'Conduta inicial diante de hemorragia pós-operatória leve em consultório?',
      'Analgesia não farmacológica e orientações de autocuidado (sem prescrição específica)?'
    ],
    documentacao: [
      'Checklist de consentimento e documentação mínima do prontuário?',
      'Boas práticas para registro fotográfico e armazenamento seguro de imagens clínicas?',
      'Como estruturar notas evolutivas objetivas e completas?'
    ]
  };

  const PRESETS_ALL = [
    ...PRESETS_MAP.biosseguranca,
    ...PRESETS_MAP.materiais,
    ...PRESETS_MAP.urgencias,
    ...PRESETS_MAP.documentacao
  ];

  const visiblePresets = useMemo(() => {
    if (activeTheme === 'todos') return PRESETS_ALL;
    return PRESETS_MAP[activeTheme] || [];
  }, [activeTheme]);

  // Load favorites from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      if (raw) setFavorites(JSON.parse(raw));
    } catch {}
  }, []);

  // Persist favorites
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    } catch {}
  }, [favorites]);

  const scrollToEnd = () => {
    requestAnimationFrame(() => {
      if (scrollerRef.current) {
        scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
      }
    });
  };
  useEffect(() => { scrollToEnd(); }, [messages.length, loading]);

  const buildModePrefix = () => {
    if (mode === 'curta') {
      return 'Preferência padrão: resposta CURTA, com apenas o essencial em 3-5 frases objetivas. Se o usuário pedir quantidade, extensão, linguagem ou formato específicos, siga o pedido do usuário.';
    }
    if (mode === 'guia') {
      return 'Preferência padrão: GUIA RÁPIDO, com etapas acionáveis e checklist enxuto. Se o usuário pedir quantidade, extensão, linguagem ou formato específicos, siga o pedido do usuário.';
    }
    return 'Preferência padrão: resposta DETALHADA quando isso for compatível com a solicitação. Se o usuário pedir quantidade, extensão, linguagem ou formato específicos, siga o pedido do usuário.';
  };

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setInput('');
    const userMsg = { id: 'u-' + Date.now(), role: 'user', text: q, meta: { ts: new Date().toISOString() } };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      // Injetamos a orientação de modo diretamente na pergunta para não alterar o backend.
      const questionWithMode = `${buildModePrefix()}\n\n${q}`;
      const res = await fetch(`${backendUrl}/api/ai-nlp/qna`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': 'Bearer ' + token } : {})
        },
        body: JSON.stringify({ question: questionWithMode })
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const a = (data && data.answer) ? String(data.answer) : 'Sem resposta no momento.';
      setMessages(prev => [
        ...prev,
        { id: 'a-' + Date.now(), role: 'assistant', text: a, meta: { ts: new Date().toISOString() } }
      ]);
    } catch (err) {
      toast?.addToast?.({ type: 'error', message: 'Falha ao obter resposta da IA.' });
      setMessages(prev => [
        ...prev,
        { id: 'a-' + Date.now(), role: 'assistant', text: 'Não consegui responder agora. Tente novamente.', meta: { ts: new Date().toISOString() } }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast?.addToast?.({ type: 'success', message: 'Copiado para a área de transferência.' });
    } catch {
      toast?.addToast?.({ type: 'error', message: 'Não foi possível copiar.' });
    }
  };

  const downloadText = (filename, text) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadMarkdown = (filename, md) => {
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportConversationTxt = () => {
    const lines = messages.map(m => {
      const who = m.role === 'user' ? 'Você' : 'Assistente';
      const when = new Date(m.meta?.ts || Date.now()).toLocaleString();
      return `[${when}] ${who}:\n${m.text}\n`;
    }).join('\n');
    downloadText(`oa_ia_conversa_${Date.now()}.txt`, lines);
  };

  const toMarkdown = () => {
    const md = [
      '# OdontoAssist AI — Q&A Técnico',
      '> Este relatório é informativo e não substitui exame clínico, diagnóstico ou prescrição.',
      ''
    ];
    messages.forEach(m => {
      const who = m.role === 'user' ? '**Você**' : '**Assistente**';
      const when = new Date(m.meta?.ts || Date.now()).toLocaleString();
      md.push(`### ${who} — ${when}`);
      md.push('');
      md.push((m.text || '').replace(/\r\n/g, '\n'));
      md.push('');
    });
    return md.join('\n');
  };

  const exportConversationMd = () => {
    downloadMarkdown(`oa_ia_conversa_${Date.now()}.md`, toMarkdown());
  };

  const printAsPdf = () => {
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) return;
    const html = `
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>OdontoAssist AI - Q&A</title>
<style>
  body { font-family: Arial, sans-serif; padding: 24px; }
  h1 { font-size: 18px; margin-top: 0; }
  .msg { margin-bottom: 16px; }
  .who { font-weight: bold; }
  .ts { color: #666; font-size: 12px; }
  pre { white-space: pre-wrap; }
  .note { margin: 12px 0; padding: 8px; background: #fff7e6; border: 1px solid #ffe8b3; }
</style>
</head>
<body>
  <h1>OdontoAssist AI — Q&A Técnico</h1>
  <div class="note">Este relatório é informativo e não substitui exame clínico, diagnóstico ou prescrição.</div>
  ${messages.map(m => `
    <div class="msg">
      <div class="who">${m.role === 'user' ? 'Você' : 'Assistente'}</div>
      <div class="ts">${new Date(m.meta?.ts || Date.now()).toLocaleString()}</div>
      <pre>${(m.text || '').replace(/[&<>]/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]))}</pre>
    </div>
  `).join('')}
  <script>window.print();</script>
</body>
</html>`;
    win.document.write(html);
    win.document.close();
  };

  const toggleFavorite = (msg) => {
    if (msg.role !== 'assistant') return;
    const key = msg.meta?.ts + '|' + (msg.text?.slice(0,48) || '');
    const exists = favorites.find(f => f.key === key);
    if (exists) {
      setFavorites(favorites.filter(f => f.key !== key));
      toast?.addToast?.({ type: 'info', message: 'Removido dos favoritos.' });
    } else {
      const prevUser = [...messages].reverse().find(m => m.role === 'user');
      const item = {
        key,
        ts: msg.meta?.ts,
        question: prevUser?.text || '',
        answer: msg.text || ''
      };
      setFavorites([item, ...favorites].slice(0, 100)); // limita a 100
      toast?.addToast?.({ type: 'success', message: 'Adicionado aos favoritos.' });
    }
  };

  const isFavorited = (msg) => {
    const key = msg.meta?.ts + '|' + (msg.text?.slice(0,48) || '');
    return !!favorites.find(f => f.key === key);
  };

  const filteredFavorites = useMemo(() => {
    const q = favQuery.trim().toLowerCase();
    if (!q) return favorites;
    return favorites.filter(f =>
      (f.question || '').toLowerCase().includes(q) ||
      (f.answer || '').toLowerCase().includes(q)
    );
  }, [favorites, favQuery]);

  const renderInlineMarkdown = (text, lineKey) => {
    return String(text || '').split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
        return <strong key={`${lineKey}-bold-${index}`}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={`${lineKey}-text-${index}`}>{part}</React.Fragment>;
    });
  };

  const renderSafeMarkdownText = (text, keyPrefix) => {
    return String(text || '').replace(/\r\n/g, '\n').split('\n').map((line, index) => {
      const lineKey = `${keyPrefix}-line-${index}`;
      if (!line.trim()) return <div key={lineKey} className="h-3" />;
      if (/^\s*---+\s*$/.test(line)) return <hr key={lineKey} className="my-3 border-gray-300" />;

      const heading = line.match(/^\s*(#{1,4})\s+(.+)$/);
      if (heading) {
        const headingClass = heading[1].length <= 2
          ? 'font-semibold text-base mt-3 mb-1 text-left'
          : 'font-semibold text-sm mt-2 mb-1 text-left';
        return (
          <div key={lineKey} className={headingClass}>
            {renderInlineMarkdown(heading[2], lineKey)}
          </div>
        );
      }

      const bullet = line.match(/^\s*[-*]\s+(.+)$/);
      if (bullet) {
        return (
          <div key={lineKey} className="flex items-start gap-2 pl-2 text-justify">
            <span aria-hidden="true" className="shrink-0">•</span>
            <span className="flex-1">{renderInlineMarkdown(bullet[1], lineKey)}</span>
          </div>
        );
      }

      const ordered = line.match(/^\s*(\d+)[.)]\s+(.+)$/);
      if (ordered) {
        return (
          <div key={lineKey} className="flex items-start gap-2 pl-2 text-justify">
            <span className="shrink-0">{ordered[1]}.</span>
            <span className="flex-1">{renderInlineMarkdown(ordered[2], lineKey)}</span>
          </div>
        );
      }

      const quote = line.match(/^\s*>\s?(.*)$/);
      if (quote) {
        return (
          <div key={lineKey} className="my-2 border-l-4 border-yellow-300 bg-yellow-50 px-3 py-2 text-justify">
            {renderInlineMarkdown(quote[1], lineKey)}
          </div>
        );
      }

      return (
        <div key={lineKey} className="text-justify">
          {renderInlineMarkdown(line, lineKey)}
        </div>
      );
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Aviso de segurança */}
      <div className="mb-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm flex gap-3 shadow-sm">
        <ShieldAlert className="w-5 h-5 mt-0.5" />
        <div>
          Este assistente não acessa prontuários nem dados de pacientes. As respostas são informativas e não substituem exame clínico, diagnóstico formal ou prescrição.
        </div>
      </div>

      {/* Barra de ferramentas */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700">Modo de resposta:</label>
          <select
            className="border rounded-md px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
          >
            <option value="curta">Curta</option>
            <option value="detalhada">Detalhada</option>
            <option value="guia">Guia rápido</option>
          </select>
        </div>
        <div className="md:ml-auto">
          <details className="relative">
            <summary className="cursor-pointer list-none text-xs px-3 py-2 rounded-md bg-white border shadow-sm hover:bg-gray-50 inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Exportar conversa
            </summary>
            <div className="absolute right-0 z-20 mt-2 w-48 rounded-lg border bg-white p-2 shadow-lg">
              <button type="button" onClick={exportConversationTxt} className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50">Baixar em TXT</button>
              <button type="button" onClick={exportConversationMd} className="w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-50">Baixar em Markdown</button>
              <button type="button" onClick={printAsPdf} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-gray-50"><Printer className="w-4 h-4" /> Imprimir / PDF</button>
            </div>
          </details>
        </div>
      </div>

      {/* Temas (chips) */}
      <div className="flex flex-wrap gap-2 mb-3">
        {Object.keys(THEMES).map(key => (
          <button
            key={key}
            onClick={() => setActiveTheme(key)}
            type="button"
            className={`text-xs px-3 py-1 rounded-full border shadow-sm transition
              ${activeTheme === key ? 'bg-blue-600 text-white border-blue-700' : 'bg-white hover:bg-gray-50'}`}
            title={`Filtrar por: ${THEMES[key]}`}
          >
            <Sparkles className="inline w-3 h-3 mr-1" /> {THEMES[key]}
          </button>
        ))}
      </div>

      {/* Presets visíveis conforme tema */}
      <div className="flex flex-wrap gap-2 mb-4">
        {visiblePresets.map((p, idx) => (
          <button
            key={idx}
            onClick={() => setInput(p)}
            className="text-xs px-3 py-2 rounded-full border bg-white shadow-sm hover:bg-gray-50"
            type="button"
            title="Inserir exemplo"
          >
            {p}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chat (2 colunas) */}
        <div className="lg:col-span-2">
          {/* Chat window */}
          <div ref={scrollerRef} className="h-[58vh] overflow-y-auto rounded-lg border bg-white p-4 shadow-sm">
            {messages.map((m) => (
              <div key={m.id} className={`mb-4 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`${m.role === 'user' ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'} border rounded-lg px-3 py-2 max-w-[80%] shadow-sm`}>
                  <div className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                    <span className="font-medium">{m.role === 'user' ? 'Você' : 'Assistente'}</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {new Date(m.meta?.ts || Date.now()).toLocaleString()}
                    </span>
                    {m.role === 'assistant' && (
                      <>
                        <button
                          className="ml-2 text-blue-700 hover:underline inline-flex items-center gap-1"
                          onClick={() => copyToClipboard(m.text)}
                          title="Copiar resposta"
                          type="button"
                        >
                          <Copy className="inline w-3 h-3" /> Copiar
                        </button>
                        <button
                          className="ml-2 text-yellow-700 hover:underline inline-flex items-center gap-1"
                          onClick={() => toggleFavorite(m)}
                          title={isFavorited(m) ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                          type="button"
                        >
                          {isFavorited(m) ? <Star className="inline w-3 h-3" /> : <StarOff className="inline w-3 h-3" />}
                          {isFavorited(m) ? 'Favorito' : 'Favoritar'}
                        </button>
                      </>
                    )}
                  </div>
                  <div className={`text-sm ${m.role === 'assistant' ? 'text-justify' : ''}`}>
                    {m.role === 'assistant'
                      ? renderSafeMarkdownText(m.text, m.id)
                      : <div className="whitespace-pre-wrap">{m.text}</div>}
                  </div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 inline animate-spin mr-1" />
                Gerando resposta…
              </div>
            )}
          </div>

          {/* Input */}
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              className="flex-1 border rounded-lg px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              placeholder="Digite sua pergunta técnica…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
            />
            <button
              type="button"
              onClick={send}
              disabled={loading || !input.trim()}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white shadow hover:bg-blue-700"
            >
              <Send className="w-4 h-4" />
              Perguntar
            </button>
          </div>
        </div>

        {/* Favoritos (1 coluna) */}
        <div className="lg:col-span-1">
          <div className="border rounded-lg bg-white p-3 h-[58vh] flex flex-col shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="font-medium">Favoritos</div>
              <Filter className="w-4 h-4 text-gray-500" />
            </div>
            <div className="text-xs text-gray-500 mb-2">Guarde respostas úteis para consulta rápida.</div>
            <div className="mb-2 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2" />
                <input
                  className="w-full pl-8 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="Buscar em favoritos…"
                  value={favQuery}
                  onChange={(e) => setFavQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1 space-y-2">
              {filteredFavorites.length === 0 && (
                <div className="text-sm text-gray-500">Nenhum favorito encontrado.</div>
              )}
              {filteredFavorites.map((f, idx) => (
                <div key={idx} className="border rounded p-2 shadow-sm">
                  <div className="text-[11px] text-gray-500 mb-1">{new Date(f.ts || Date.now()).toLocaleString()}</div>
                  <div className="text-sm font-medium mb-1">Pergunta</div>
                  <div className="text-sm whitespace-pre-wrap mb-2">{f.question || '—'}</div>
                  <div className="text-sm font-medium mb-1">Resposta</div>
                  <div className="text-sm text-justify">{renderSafeMarkdownText(f.answer || '—', `favorite-${f.key}`)}</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      className="text-xs px-3 py-1 rounded-md bg-white border shadow-sm hover:bg-gray-50 inline-flex items-center gap-1"
                      onClick={() => {
                        const text = `Pergunta:\n${f.question}\n\nResposta:\n${f.answer}`;
                        const md = `# Favorito — OdontoAssist AI\n\n**Pergunta**\n\n${f.question}\n\n**Resposta**\n\n${f.answer}\n`;
                        downloadText(`oa_ia_favorito_${idx}.txt`, text);
                        downloadMarkdown(`oa_ia_favorito_${idx}.md`, md);
                      }}
                      type="button"
                    >
                      <Download className="w-3 h-3" /> TXT/MD
                    </button>
                    <button
                      className="text-xs px-3 py-1 rounded-md bg-white border shadow-sm hover:bg-gray-50 inline-flex items-center gap-1"
                      onClick={() => copyToClipboard(f.answer)}
                      type="button"
                    >
                      <Copy className="w-3 h-3" /> Copiar
                    </button>
                    <button
                      className="text-xs px-3 py-1 rounded-md bg-white border shadow-sm hover:bg-gray-50 inline-flex items-center gap-1"
                      onClick={() => setFavorites(favorites.filter((x) => x.key !== f.key))}
                      type="button"
                    >
                      <StarOff className="w-3 h-3" /> Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChatAssistant;
